import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Eye, EyeOff, BarChart2, TrendingUp, Bell, Shield, Zap, ChevronRight } from "lucide-react";
import { setAuth } from "../../store/authSlice";
import api from "../../utils/api";
import { toast } from "sonner";
import Footer from "../../components/Footer";

const FEATURES = [
  { icon: TrendingUp, text: "AI-powered revenue & cash flow forecasting" },
  { icon: BarChart2, text: "Unified financial, CRM & banking dashboard" },
  { icon: Bell, text: "Smart threshold alerts & anomaly detection" },
  { icon: Shield, text: "Bank-grade security with full audit trail" },
];

const DEMO_ACCOUNTS = [
  { role: "Super Admin", email: "super@onpoint.bi", password: "SuperAdmin123!" },
  { role: "Business Owner", email: "owner@acme.com", password: "Owner123!" },
  { role: "CFO/Consultant", email: "cfo@acme.com", password: "CFO123!" },
  { role: "Partner", email: "partner@brightedge.io", password: "Partner123!" },
];

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      localStorage.setItem("onpoint_token", res.data.access_token);
      localStorage.setItem("onpoint_refresh", res.data.refresh_token);
      dispatch(setAuth({ user: res.data.user, token: res.data.access_token, refreshToken: res.data.refresh_token }));
      toast.success(`Welcome back, ${res.data.user.name}!`);
      // Route partner users to the Partner Portal
      if (res.data.user.role === "consulting_partner") {
        navigate("/partner/dashboard");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex overflow-hidden">
      {/* Left Hero Panel - Dark Navy from Figma */}
      <div className="hidden lg:flex lg:w-5/12 relative flex-col justify-between p-12 overflow-hidden" style={{ backgroundColor: "#282560" }}>
        {/* Subtle radial glow */}
        <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(circle at 30% 50%, #1B75BA 0%, transparent 60%)" }} />
        <div className="absolute bottom-0 right-0 w-64 h-64 opacity-5" style={{ background: "radial-gradient(circle, #B8D8F2 0%, transparent 70%)" }} />

        {/* Centered Logo */}
        <div className="relative z-10" />

        <div className="relative z-10 flex flex-col items-center text-center">
          <img src="/images/logo-white.png" alt="OnPoint BI" className="w-72 h-auto mb-10" data-testid="login-hero-logo" />
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-6" style={{ backgroundColor: "rgba(0,127,217,0.2)", border: "1px solid rgba(188,218,241,0.3)" }}>
            <Zap className="w-3 h-3" style={{ color: "#B8D8F2" }} />
            <span className="text-xs font-medium" style={{ color: "#B8D8F2" }}>Real Intelligence for Real Businesses</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-3 leading-tight" style={{ fontFamily: "Inter, sans-serif" }}>
            Welcome back!
          </h1>
          <p className="text-white/60 text-sm mb-8 leading-relaxed max-w-xs">Sign in to access your unified business intelligence dashboard</p>

          <div className="space-y-3 text-left w-full max-w-xs">
            {FEATURES.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "rgba(188,218,241,0.15)", border: "1px solid rgba(188,218,241,0.2)" }}>
                  <Icon className="w-3.5 h-3.5" style={{ color: "#B8D8F2" }} />
                </div>
                <span className="text-white/70 text-sm">{text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10">
          <p className="text-white/25 text-xs">&copy; 2026 OnPointBI. All rights reserved.</p>
        </div>
      </div>

      {/* Right Form Panel - White */}
      <div className="w-full lg:w-7/12 flex items-center justify-center p-6 lg:p-14 bg-white">
        <div className="w-full max-w-md animate-fade-in">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img src="/images/logo-colored.png" alt="OnPoint BI" className="w-56 h-auto" data-testid="login-mobile-logo" />
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-1.5" style={{ color: "#1C2040", fontFamily: "Inter, sans-serif" }}>
              Access your OnPoint Account
            </h2>
            <p className="text-sm" style={{ color: "#6B7491" }}>Boost Your Business Up To The Next Level!</p>
          </div>

          {/* Demo credentials */}
          <div className="mb-6 p-3.5 rounded-xl" style={{ backgroundColor: "#F7F8FA", border: "1px solid #CDD2DE" }}>
            <p className="text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: "#6B7491" }}>Demo Accounts</p>
            {DEMO_ACCOUNTS.map((d) => (
              <button key={d.role} type="button" data-testid={`demo-${d.role.toLowerCase().replace(/[^a-z]/g, "-")}`}
                onClick={() => { setEmail(d.email); setPassword(d.password); }}
                className="w-full flex items-center justify-between py-1.5 hover:opacity-75 transition-opacity">
                <span className="text-xs font-medium" style={{ color: "#1C2040" }}>{d.role}</span>
                <span className="text-xs font-mono" style={{ color: "#6B7491" }}>{d.email}</span>
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: "#1C2040" }}>Email</label>
              <input data-testid="login-email-input" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="Your Email Address" required
                className="w-full h-11 px-4 rounded-lg text-sm outline-none transition-all"
                style={{ backgroundColor: "#F7F8FA", border: "1.5px solid #D9D9D9", color: "#1C2040" }}
                onFocus={(e) => e.target.style.borderColor = "#1B75BA"}
                onBlur={(e) => e.target.style.borderColor = "#D9D9D9"}
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label className="block text-sm font-medium" style={{ color: "#1C2040" }}>Password</label>
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-medium hover:underline" style={{ color: "#1B75BA" }}>
                  Forgot Password
                </Link>
              </div>
              <div className="relative">
                <input data-testid="login-password-input" type={showPass ? "text" : "password"} value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="Your Password" required
                  className="w-full h-11 px-4 pr-12 rounded-lg text-sm outline-none transition-all"
                  style={{ backgroundColor: "#F7F8FA", border: "1.5px solid #D9D9D9", color: "#1C2040" }}
                  onFocus={(e) => e.target.style.borderColor = "#1B75BA"}
                  onBlur={(e) => e.target.style.borderColor = "#D9D9D9"}
                />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: "#6B7491" }}>
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input type="checkbox" id="remember" className="w-4 h-4 rounded" style={{ accentColor: "#1B75BA" }} />
              <label htmlFor="remember" className="text-sm" style={{ color: "#6B7491" }}>Remember me</label>
            </div>

            <button data-testid="login-submit-btn" type="submit" disabled={loading}
              className="w-full h-11 rounded-lg font-semibold text-sm text-white transition-all active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ backgroundColor: "#1B75BA" }}
              onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = "#125693")}
              onMouseLeave={(e) => (e.target.style.backgroundColor = "#1B75BA")}
            >
              {loading ? "Signing in..." : (<>Login <ChevronRight className="w-4 h-4" /></>)}
            </button>
          </form>

          <p className="mt-6 text-center text-sm" style={{ color: "#6B7491" }}>
            Don't have an account?{" "}
            <Link to="/register" data-testid="go-to-register-link" className="font-semibold hover:underline" style={{ color: "#1B75BA" }}>
              Sign up
            </Link>
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 lg:left-auto lg:w-7/12">
          <Footer />
        </div>
      </div>
    </div>
  );
}
